import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';

import {
  Botao,
  BotaoSelecao,
  Checkbox,
  Field,
  FormActions,
  FormSection,
  Input,
  Select,
} from '../../ui';

import ModalMarcas from '../modal/marcas/ModalMarcas';
import ModalVeiculos from '../modal/veiculos/ModalVeiculos';
import ModalCores from '../modal/cores/ModalCores';
import ModalCombustiveis from '../modal/combustiveis/ModalCombustiveis';
import ModalTipoVeiculos from '../modal/tipo-veiculo/ModalTipoVeiculos';
import ModalCategorias from '../modal/categorias/ModalCategorias';
import ModalCooperativasUsuario from '../modal/cooperativas-usuario/ModalCooperativasUsuario';
import ModalCotas from '../modal/cotas/ModalCotas';

import { UFS, formatCep, formatPlaca, placaValida } from '../../utils/mascaras';
import { anosDisponiveis } from '../../utils/anosVeiculo';
import { digitarMoeda, normalizarMoeda, numeroParaMoeda, divergeAcimaDoLimiar } from '../../utils/moeda';
import {
  TIPO_VEICULO_ELETRICO,
  TIPO_VEICULO_ELETRICO_NOME,
} from '../../utils/veiculoEletrico';

/**
 * O formulário do veículo — **um componente para as três telas** (at-59, frente C/C2).
 *
 * ## Por que existe
 *
 * Medido em 09/09/2026: `carro.tsx` (991 linhas), `carroEdit.tsx` (1.308) e
 * `carroSubstituir.tsx` (1.401) declaravam **o mesmo formulário**: os mesmos ~30
 * campos de `carroData`, os mesmos 8 modais com os mesmos 24 handlers de
 * abrir/fechar/selecionar, a mesma `isFormValid`, a mesma `handleChange` com o
 * mesmo tratamento de placa e de moeda. **3.769 linhas** viram este arquivo e três
 * telas finas.
 *
 * E não é só volume: enquanto eram três cópias, **toda correção tinha de ser feita
 * três vezes** — foi assim que a regra da motocicleta elétrica (at-46/at-54), a do
 * valor protegido (at-30) e a da cota por faixa acabaram triplicadas, cada uma com
 * as suas pequenas divergências de texto.
 *
 * ## 🔴 Contratos que este componente NÃO pode mudar
 *
 * As chaves que ele devolve em {@link DadosVeiculo} são lidas **por nome** pelas
 * telas seguintes: `views/documento` monta com elas o payload de
 * `POST /associado/cadastrar`, `views/documento/documentoEdit` a substituição, e
 * `/produto` e `/termo` leem `valorCota`. Elas são também o formato dos
 * **rascunhos já gravados no banco** (at-38). Renomear qualquer uma quebra o
 * cadastro em silêncio.
 *
 * ⚠️ **`valorCota` parece opcional e NÃO é.** Ele não estava no literal do estado
 * das telas antigas — era enxertado por `handleSelectCota` — e é o que vira
 * `taxaAdministrativa` no termo e a parcela "cota" da mensalidade em `/produto`.
 * Aqui ele é declarado no tipo justamente para parar de depender daquele truque.
 *
 * ## Camada
 *
 * Mora em `components/` e não em `src/ui` porque conhece o domínio: os 8 modais do
 * SGA, o catálogo de motocicletas elétricas e as regras de cota/valor protegido. O
 * kit que ele usa é puro — precedente do `FormularioAssociado` (`13-ui-kit.md`).
 */

export interface EnderecoVeiculo {
  rua: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
}

/** O que o formulário devolve — as chaves do estado do fluxo (`associadoSlice.Carro`). */
export interface DadosVeiculo {
  placa: string;
  anoModelo: number;
  anoFabricacao: number;
  marcaId: string;
  marcaNome: string;
  modelo: string;
  codigoFipe: string;
  chassi: string;
  renavam: string;
  corId: string;
  corNome: string;
  combustivelId: string;
  combustivelNome: string;
  cambioId: string;
  cambioNome: string;
  tipoVeiculoId: string;
  tipoVeiculoNome: string;
  categoriaId: string;
  categoriaNome: string;
  cooperativaId: string;
  cooperativaNome: string;
  cotaId: string;
  cotaNome: string;
  cotaDescricao: string;
  /** ⚠️ Alimenta a mensalidade de `/produto` e a taxa administrativa do termo. */
  valorCota?: number | string;
  vencimentoDesejado: number;
  valorFipe: string;
  valorProtegido: string;
  /**
   * Motocicleta elétrica (at-46, item 50): preenchido = o veículo é uma bike do
   * catálogo — some o FIPE, a placa é gerada pelo backend e o protegido é a única
   * base. `bikeMarcaId` é só para o modal de modelos saber que deve listar o
   * catálogo em vez da FIPE.
   */
  bikeEletricaId: string;
  bikeMarcaId: string;
  /**
   * Veículo 0km (at-81): ainda sem placa e sem renavam. O backend grava a
   * identificação provisória `0KM-<chassi>` e o SGA recebe os dois vazios; a placa
   * real entra depois, pelo "Informar placa" da Carteira.
   */
  zeroKm?: boolean;
  endereco: EnderecoVeiculo;
}

/**
 * Um veículo que o SAAC sabe estar no SGA — o que trava o valor protegido (at-30).
 *
 * ⚠️ **A trava segue o VEÍCULO (placa + `codigoFipe` + bike), não só a placa.**
 * Travar pela placa prendia a tela de substituição inteira (ela pré-carrega o
 * veículo antigo) e, pior, fazia o veículo novo nascer com o valor protegido do
 * antigo. Escolher outro modelo é outro veículo, e libera o campo.
 */
export interface VeiculoNoSga {
  placa: string;
  codigoFipe: string;
  bikeEletricaId: string;
  /** Valor persistido — repõe no campo travado o que está no banco, não a FIPE. */
  valorProtegido?: number | null;
}

const ENDERECO_VAZIO: EnderecoVeiculo = {
  rua: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
  cep: '',
};

const VAZIO: DadosVeiculo = {
  placa: '',
  anoModelo: 0,
  anoFabricacao: 0,
  marcaId: '',
  marcaNome: '',
  modelo: '',
  codigoFipe: '',
  chassi: '',
  renavam: '',
  corId: '',
  corNome: '',
  combustivelId: '',
  combustivelNome: '',
  cambioId: '',
  cambioNome: '',
  tipoVeiculoId: '',
  tipoVeiculoNome: '',
  categoriaId: '',
  categoriaNome: '',
  cooperativaId: '',
  cooperativaNome: '',
  cotaId: '',
  cotaNome: '',
  cotaDescricao: '',
  vencimentoDesejado: 0,
  valorFipe: '',
  valorProtegido: '',
  bikeEletricaId: '',
  bikeMarcaId: '',
  zeroKm: false,
  endereco:ENDERECO_VAZIO,
};

/** Campos que podem exibir erro — as chaves do `data-field` e do `Field`. */
type CampoVeiculo =
  | 'placa'
  | 'anoModelo'
  | 'anoFabricacao'
  | 'marcaId'
  | 'modelo'
  | 'chassi'
  | 'renavam'
  | 'cooperativaId'
  | 'corId'
  | 'combustivelId'
  | 'tipoVeiculoId'
  | 'categoriaId'
  | 'cambioId'
  | 'valorFipe'
  | 'valorProtegido'
  | 'cotaId'
  | 'vencimentoDesejado'
  | 'cep'
  | 'rua'
  | 'numero'
  | 'bairro'
  | 'cidade'
  | 'estado';

type Erros = Partial<Record<CampoVeiculo, string>>;

/**
 * O "valor base" que segue para o slice `codigo` e, dali, para produtos e termo.
 *
 * É o **valor FIPE** no veículo comum e o **protegido** na motocicleta elétrica,
 * que não tem FIPE (at-46).
 *
 * ⚠️ **A limpeza é estrutural e o resultado é STRING** — não trocar por
 * `parseMoeda` (que devolve número) sem conferir os consumidores: o slice `codigo`
 * guarda o que recebe. E jamais usar `/\D/g` + `/100`, que leria "80668" como
 * 806,68 (a armadilha da at-30).
 */
export function valorBaseDoVeiculo(dados: DadosVeiculo): string {
  const bruto = dados.bikeEletricaId ? dados.valorProtegido : dados.valorFipe;
  if (!bruto) return '';

  return bruto.replace(/R\$\s?/, '').replace(/\./g, '').replace(',', '.');
}

/** O que a tela pode empurrar para dentro do formulário depois de montado. */
export interface HandleFormularioVeiculo {
  /**
   * Aplica um veículo carregado (busca por placa / importação do SGA).
   *
   * ⚠️ Existe porque a carga vem **depois** do mount: `valoresIniciais` é lido uma
   * única vez, de propósito (ver o comentário no `useState`). Sem isto, a tela de
   * substituição de titular teria de virar controlada e devolver os ~30 campos de
   * estado para dentro dela — o oposto do que esta branch faz.
   */
  preencher: (parcial: Partial<DadosVeiculo>) => void;
}

export interface PropsFormularioVeiculo {
  /** Título do primeiro bloco — muda em cada um dos três fluxos. */
  tituloIdentificacao?: string;
  /** Rótulo da ação principal (ex.: "Continuar — Documentos"). */
  rotuloAcao: string;
  /** Preenchimento inicial. Ausente = formulário vazio. */
  valoresIniciais?: Partial<DadosVeiculo> | null;
  /** Regional do associado — precondição da cota, não é campo desta tela. */
  regionalId: string;
  /** Endereço do titular, para o "Mesmo endereço de cobrança?". */
  enderecoAssociado: EnderecoVeiculo;
  /**
   * Veículos que já estão no SGA. O formulário procura aqui o que casa com o que
   * está na tela para decidir a trava do valor protegido (at-30).
   */
  veiculosNoSga?: VeiculoNoSga[];
  /** Faixa acima do formulário — a divergência do SGA na substituição de titular. */
  aviso?: React.ReactNode;
  /** Bloco antes do formulário — "Dados do Veículo a ser Substituído". */
  blocoAntes?: React.ReactNode;
  /**
   * Ação sob o campo de placa (render prop porque precisa do valor digitado): o
   * "Buscar Veículo" e a oferta de importação do SGA da substituição de titular.
   */
  acaoPlaca?: (placa: string) => React.ReactNode;
  /** Texto sob a Identificação quando o veículo é uma motocicleta elétrica. */
  dicaIdentificacaoBike?: React.ReactNode;
  /** Mensagem do valor protegido travado — o desfecho sugerido muda por fluxo. */
  mensagemValorTravado?: string;
  /**
   * Validação que só a tela conhece (ex.: o código do veículo a substituir).
   * Devolve a mensagem de erro, ou `null` quando está tudo certo.
   */
  validacaoExtra?: () => string | null;
  aoSalvar: (dados: DadosVeiculo) => void;
}

/**
 * Os anos de fabricação possíveis para um dado ano de modelo.
 *
 * Um veículo é fabricado **no ano do seu modelo ou no anterior** — a montadora
 * produz no fim do ano N e vende como modelo N+1. Não existe fabricação depois do
 * modelo, nem folga maior que um ano. Antes, os dois campos ofereciam os mesmos 31
 * anos de forma independente, e nada impedia "modelo 2024, fabricação 2015".
 *
 * ⚠️ **A restrição vale só neste sentido.** Filtrar também o Modelo pelo ano de
 * Fabricação criaria um impasse: com fabricação 2023, o modelo ficaria preso a
 * {2023, 2024} e o consultor não conseguiria mais corrigi-lo para 2020. O Modelo é
 * o campo primário — é ele que consulta a FIPE no `ModalVeiculos`.
 *
 * ⚠️ **`selecionado` fora da regra CONTINUA na lista.** Cadastro antigo (ou
 * importado do SGA) pode ter a combinação impossível, e a tela de substituição
 * pré-carrega esses dados: removê-lo faria o campo aparecer **vazio**, e o
 * consultor concluiria que o dado se perdeu.
 */
export function anosDeFabricacaoPossiveis(
  anoModelo: number,
  todosOsAnos: string[],
  selecionado = 0,
): string[] {
  if (!anoModelo) return todosOsAnos;

  const possiveis = [String(anoModelo), String(anoModelo - 1)];
  if (selecionado && !possiveis.includes(String(selecionado))) possiveis.push(String(selecionado));

  return possiveis;
}

const FormularioVeiculo = forwardRef<HandleFormularioVeiculo, PropsFormularioVeiculo>(
  (
    {
      tituloIdentificacao = 'Dados do Veículo',
      rotuloAcao,
      valoresIniciais,
      regionalId,
      enderecoAssociado,
      veiculosNoSga,
      aviso,
      blocoAntes,
      acaoPlaca,
      dicaIdentificacaoBike,
      mensagemValorTravado = 'Veículo já enviado ao SGA — valor protegido não pode ser alterado. Selecione outro modelo se for uma substituição.',
      validacaoExtra,
      aoSalvar,
    },
    ref,
  ) => {
    /*
     * ⚠️ O estado nasce dos `valoresIniciais` **uma vez** (inicializador do
     * `useState`), e não por `useEffect` que sincroniza — mesma decisão do
     * `FormularioAssociado`: um efeito que reescrevesse o estado a cada mudança da
     * prop apagaria o que o consultor está digitando. Carga posterior (busca por
     * placa, importação do SGA) entra pelo `preencher` do handle imperativo.
     */
    const [form, setForm] = useState<DadosVeiculo>(() =>
      valoresIniciais ? { ...VAZIO, ...valoresIniciais } : VAZIO,
    );

    const [erros, setErros] = useState<Erros>({});
    const [salvando, setSalvando] = useState(false);
    const [usarEnderecoDoAssociado, setUsarEnderecoDoAssociado] = useState(false);
    /** Mês de referência da FIPE do modelo escolhido — **só exibição** (at-21). */
    const [referenciaFipe, setReferenciaFipe] = useState('');
    const formRef = useRef<HTMLFormElement>(null);

    const [modalAberto, setModalAberto] = useState<
      | null
      | 'marcas'
      | 'veiculos'
      | 'cores'
      | 'combustiveis'
      | 'tipos'
      | 'categorias'
      | 'cooperativas'
      | 'cotas'
    >(null);
    const fechar = useCallback(() => setModalAberto(null), []);

    /** Escreve campos e **limpa os erros deles** — a mensagem não fica de teimosa. */
    const aplicar = useCallback((parcial: Partial<DadosVeiculo>) => {
      setForm((antes) => ({ ...antes, ...parcial }));
      setErros((antes) => {
        const chaves = Object.keys(parcial) as CampoVeiculo[];
        if (!chaves.some((k) => antes[k])) return antes;

        const proximo = { ...antes };
        chaves.forEach((k) => delete proximo[k]);
        return proximo;
      });
    }, []);

    useImperativeHandle(ref, () => ({ preencher: aplicar }), [aplicar]);

    /** Trocar de valor invalida a cota: ela é escolhida pela FAIXA do protegido. */
    const limparCota = { cotaId: '', cotaDescricao: '' };

    const ehBike = form.bikeEletricaId.trim() !== '';
    /** at-81: 0km não existe para motocicleta elétrica — ela tem caminho próprio. */
    const ehZeroKm = !ehBike && form.zeroKm === true;

    /**
     * Marcar 0km apaga placa e renavam digitados: eles não vão ao backend, e
     * deixá-los na tela faria o consultor achar que foram gravados.
     */
    const alternarZeroKm = (marcado: boolean) => {
      /*
       * at-84: o modo 0km muda o PREÇO (tabela FIPE 0km × usado do ano), então o
       * modelo já escolhido é limpo nos dois sentidos — mantê-lo deixaria o preço
       * de um modo valendo no outro. Mesma regra de `definirAno`. O protegido sai
       * junto porque nasce do preço FIPE (at-30), e a cota porque segue a faixa dele.
       */
      const zeraModelo = form.codigoFipe
        ? { modelo: '', codigoFipe: '', valorFipe: '', valorProtegido: '', ...limparCota }
        : {};
      if (form.codigoFipe) setReferenciaFipe('');
      aplicar(
        marcado
          ? { zeroKm: true, placa: '', renavam: '', ...zeraModelo }
          : { zeroKm: false, ...zeraModelo },
      );
      setErros((atuais) => ({ ...atuais, placa: undefined, renavam: undefined }));
    };

    /*
     * at-30: o formulário ainda descreve o mesmo veículo que está no SGA (placa E
     * modelo) → valor protegido travado. Trocar o modelo ou a placa = veículo novo.
     *
     * ⚠️ Para bicicleta (at-46) o "modelo" é o id do catálogo, não o código FIPE —
     * por isso os três campos entram na comparação.
     */
    const veiculoTravado = useMemo(
      () =>
        (veiculosNoSga ?? []).find(
          (v) =>
            v.placa === form.placa &&
            v.codigoFipe === (form.codigoFipe || '') &&
            v.bikeEletricaId === (form.bikeEletricaId || ''),
        ) ?? null,
      [veiculosNoSga, form.placa, form.codigoFipe, form.bikeEletricaId],
    );
    const valorProtegidoTravado = veiculoTravado !== null;

    /*
     * Com o campo travado, mostra o valor protegido PERSISTIDO — sem isto o
     * payload iria com a FIPE do modelo e o backend rejeitaria a "alteração".
     */
    useEffect(() => {
      if (veiculoTravado?.valorProtegido == null) return;

      const mascarado = numeroParaMoeda(Number(veiculoTravado.valorProtegido));
      setForm((antes) =>
        antes.valorProtegido === mascarado ? antes : { ...antes, valorProtegido: mascarado },
      );
    }, [veiculoTravado?.placa, veiculoTravado?.valorProtegido]);

    // ── Handlers de campo ─────────────────────────────────────────────────

    const definirPlaca = (bruto: string) => {
      const limpa = formatPlaca(bruto);

      // Placa completa e fora dos dois formatos: a tecla é recusada, como antes.
      if (limpa.length === 7 && !placaValida(limpa)) return;

      aplicar({ placa: limpa });
    };

    const definirAno = (campo: 'anoModelo' | 'anoFabricacao', valor: string) => {
      const numero = parseInt(valor, 10) || 0;

      if (campo === 'anoFabricacao') {
        aplicar({ anoFabricacao: numero });
        return;
      }

      // O modelo FIPE é indexado por ano; o catálogo de bicicletas não (at-46) —
      // trocar o ano de uma bike não pode desfazer a escolha do modelo.
      const zeraModelo = form.bikeEletricaId ? {} : { modelo: '', codigoFipe: '', valorFipe: '' };

      /*
       * Trocar o ano do modelo pode deixar a fabricação impossível (modelo 2020
       * com fabricação 2023). Nesse caso ela é limpa, e não mantida à espera de o
       * consultor reparar: são só duas opções para reescolher, e deixar o valor
       * antigo o faria conviver com uma lista que já não o contém.
       */
      const fabricacaoIncompativel =
        form.anoFabricacao !== 0 &&
        !anosDeFabricacaoPossiveis(numero, []).includes(String(form.anoFabricacao));

      aplicar({
        anoModelo: numero,
        ...zeraModelo,
        ...(fabricacaoIncompativel ? { anoFabricacao: 0 } : {}),
      });
    };

    const definirEndereco = (campo: keyof EnderecoVeiculo, valor: string) => {
      const formatado = campo === 'cep' ? formatCep(valor) : valor;

      setForm((antes) => ({ ...antes, endereco: { ...antes.endereco, [campo]: formatado } }));
      setErros((antes) => (antes[campo as CampoVeiculo] ? { ...antes, [campo]: undefined } : antes));
    };

    // ── Seleções vindas dos modais ────────────────────────────────────────

    const selecionarMarca = (marca: any) => {
      aplicar({
        marcaId: marca.codigo_marca,
        marcaNome: marca.descricao,
        modelo: '',
        codigoFipe: '',
        valorFipe: '',
        // Trocar de marca zera a escolha de bike; se a marca nova é de bicicleta,
        // o modal de modelos passa a listar o catálogo (at-46).
        bikeEletricaId: '',
        bikeMarcaId: marca.bike ? marca.bikeMarcaId ?? '' : '',
        /*
         * Marca de bicicleta (at-46): a placa é gerada pelo backend, e chassi e
         * renavam de um carro que estivesse carregado NÃO podem seguir — são
         * únicos e continuam com ele.
         */
        ...(marca.bike ? { placa: '', chassi: '', renavam: '', zeroKm: false } : {}),
      });
      setReferenciaFipe('');
      fechar();
    };

    const selecionarVeiculo = (veiculo: any) => {
      if (veiculo.bike) {
        /*
         * Motocicleta elétrica (at-46): não existe FIPE. O valor do catálogo vira
         * o Valor Protegido INICIAL (editável — é ele que define cota, rateio e
         * produtos); combustível, câmbio e tipo são preenchidos porque só há uma
         * resposta possível.
         *
         * 🔴 O TIPO entra aqui por decisão da at-54 (item 58): é ele que filtra a
         * lista de cotas, e o modal de tipos nem oferece o 35 (o hardcode
         * `codigoPuxar` do backend não o inclui). Sem isto o consultor escolhia
         * uma cota de carro para um veículo elétrico — e o SGA aceitava calado.
         */
        aplicar({
          modelo: veiculo.model,
          bikeEletricaId: veiculo.bikeEletricaId,
          codigoFipe: '',
          valorFipe: '',
          valorProtegido: veiculo.price,
          combustivelId: '7',
          combustivelNome: 'ELÉTRICO',
          cambioId: 'NAO_APLICAVEL',
          cambioNome: 'Não se aplica',
          tipoVeiculoId: TIPO_VEICULO_ELETRICO,
          tipoVeiculoNome: TIPO_VEICULO_ELETRICO_NOME,
          ...limparCota,
        });
        setReferenciaFipe('');
        fechar();
        return;
      }

      aplicar({
        modelo: veiculo.model,
        codigoFipe: veiculo.codeFipe,
        valorFipe: veiculo.price,
        /*
         * at-30: escolher um modelo define OUTRO veículo (é o caso da
         * substituição) — o protegido acompanha a FIPE nova e o campo destrava.
         * Congelar aqui fazia o veículo novo nascer com o valor do carro antigo.
         */
        valorProtegido: veiculo.price,
        bikeEletricaId: '',
      });
      /*
       * ⚠️ Só exibição (at-21), e **fora do `DadosVeiculo` de propósito**: o
       * `ValidationPipe` do backend usa `forbidNonWhitelisted` e derruba a
       * requisição inteira com um campo que a DTO não declara.
       */
      setReferenciaFipe(veiculo.referenceMonth || '');
      fechar();
    };

    const selecionarTipo = (tipo: any) => {
      aplicar({
        tipoVeiculoId: tipo.codigo_tipo,
        tipoVeiculoNome: tipo.descricao_tipo,
        categoriaId: '',
        categoriaNome: '',
        ...limparCota,
      });
      fechar();
    };

    const abrirCategorias = () => {
      if (!form.tipoVeiculoId) {
        toast.warn('Selecione um Tipo de Veículo antes de continuar.');
        return;
      }
      setModalAberto('categorias');
    };

    const abrirVeiculos = () => {
      if (!form.marcaId) {
        toast.warning('Selecione a Marca antes de buscar veículos.');
        return;
      }
      // A FIPE é indexada por ano; o catálogo de bicicletas não (at-46).
      if (!form.bikeMarcaId && !form.anoModelo) {
        toast.warning('Selecione o Ano do Modelo e a Marca antes de buscar veículos.');
        return;
      }
      setModalAberto('veiculos');
    };

    const abrirCotas = () => {
      const faltando: string[] = [];

      if (!regionalId) faltando.push('Regional');
      if (!form.cooperativaId) faltando.push('Cooperativa');
      if (!form.tipoVeiculoId) faltando.push('Tipo de veículo');
      // A cota é escolhida pela faixa do valor PROTEGIDO (at-30); a bike nem tem FIPE.
      if (!(form.valorProtegido || form.valorFipe)) faltando.push('Valor protegido');

      if (faltando.length > 0) {
        toast.warning(
          `Antes de selecionar a cota, preencha: ${faltando.join(', ')}.`,
        );
        return;
      }

      setModalAberto('cotas');
    };

    // ── Validação e submit ────────────────────────────────────────────────

    /**
     * ⚠️ Antes da at-59 não havia validação por campo: o botão "Salvar" ficava
     * **desabilitado** enquanto `isFormValid()` fosse falso, e pronto. Com ~20
     * obrigatórios espalhados por três blocos, "não consigo salvar e não sei por
     * quê" era o estado normal — o mesmo defeito que a spec 03 §4 nomeia para a
     * etapa 1 e que a C1 corrigiu lá.
     */
    const validar = (): Erros => {
      const e: Erros = {};
      const obrigatorio = (v: string) => (v.trim() === '' ? 'Campo obrigatório' : undefined);

      /*
       * Motocicleta elétrica (at-46): sem placa (o backend gera), sem renavam, sem
       * câmbio, sem categoria (o tipo 35 não tem no SGA) e sem FIPE. O chassi vira
       * o número de série do quadro, opcional.
       */
      if (!ehBike) {
        // at-81: 0km ainda não tem placa nem renavam — o chassi é o que o identifica.
        if (!ehZeroKm) {
          e.placa = obrigatorio(form.placa);
          e.renavam = obrigatorio(form.renavam);
        }
        e.chassi = obrigatorio(form.chassi);
        e.cambioId = form.cambioId.trim() ? undefined : 'Selecione o câmbio';
        e.categoriaId = form.categoriaId.trim() ? undefined : 'Selecione a categoria';
        e.valorFipe = form.valorFipe.trim() ? undefined : 'Selecione o veículo para carregar a FIPE';
      }

      e.anoModelo = form.anoModelo ? undefined : 'Selecione o ano';
      e.anoFabricacao = form.anoFabricacao ? undefined : 'Selecione o ano';
      e.marcaId = form.marcaId.trim() ? undefined : 'Selecione a marca';
      e.modelo = form.modelo.trim() ? undefined : 'Selecione o veículo';
      e.corId = form.corId.trim() ? undefined : 'Selecione a cor';
      e.combustivelId = form.combustivelId.trim() ? undefined : 'Selecione o combustível';
      e.tipoVeiculoId = form.tipoVeiculoId.trim() ? undefined : 'Selecione o tipo de veículo';
      e.cooperativaId = form.cooperativaId.trim() ? undefined : 'Selecione a cooperativa';
      e.cotaId = form.cotaId.trim() ? undefined : 'Selecione a cota';
      e.vencimentoDesejado = form.vencimentoDesejado ? undefined : 'Selecione o vencimento';
      e.valorProtegido = obrigatorio(form.valorProtegido);

      if (!usarEnderecoDoAssociado) {
        e.cep = obrigatorio(form.endereco.cep);
        e.rua = obrigatorio(form.endereco.rua);
        e.numero = obrigatorio(form.endereco.numero);
        e.bairro = obrigatorio(form.endereco.bairro);
        e.cidade = obrigatorio(form.endereco.cidade);
        e.estado = form.endereco.estado.trim() ? undefined : 'Selecione a UF';
      }

      (Object.keys(e) as CampoVeiculo[]).forEach((k) => {
        if (!e[k]) delete e[k];
      });

      return e;
    };

    const enviar = (evento: React.FormEvent) => {
      evento.preventDefault();

      // Validação da própria tela primeiro (ex.: o código do veículo a substituir):
      // ela é precondição do fluxo, não de um campo, e vale mesmo com o resto ok.
      const extra = validacaoExtra?.();
      if (extra) {
        toast.error(extra);
        return;
      }

      const encontrados = validar();
      if (Object.keys(encontrados).length > 0) {
        setErros(encontrados);

        /*
         * Rola até o primeiro erro — sem isto, num formulário de três blocos, a
         * mensagem pode nascer fora da área visível.
         *
         * ⚠️ Chamada DEFENSIVA: `scrollIntoView` não existe no jsdom nem é
         * garantido em toda WebView antiga, e uma exceção aqui abortaria o resto
         * do submit, deixando o formulário sem reação visível ao clique.
         */
        const alvo = formRef.current?.querySelector(
          `[data-field="${Object.keys(encontrados)[0]}"]`,
        );
        if (typeof (alvo as HTMLElement | null)?.scrollIntoView === 'function') {
          (alvo as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return;
      }

      // A regional vem da etapa 1 e não tem campo aqui: sem ela o backend recusa,
      // então o aviso é de fluxo (toast), não de campo.
      if (!regionalId) {
        toast.warning('Regional não definida para este associado. Volte à etapa anterior.');
        return;
      }

      setSalvando(true);

      aoSalvar({
        ...form,
        endereco: usarEnderecoDoAssociado ? enderecoAssociado : form.endereco,
      });

      /*
       * ⚠️ O `salvando` NÃO é desligado aqui: quem chama navega para a etapa
       * seguinte, e voltar o botão antes disso pisca a tela e convida a um segundo
       * clique — que gravaria um segundo rascunho.
       */
    };

    const anos = useMemo(anosDisponiveis, []);
    const anosDeFabricacao = useMemo(
      () => anosDeFabricacaoPossiveis(form.anoModelo, anos, form.anoFabricacao),
      [form.anoModelo, form.anoFabricacao, anos],
    );

    /** Rótulo e dica da placa mudam quando o veículo é uma motocicleta elétrica. */
    const dicaBikePadrao = (
      <>
        Motocicleta elétrica não tem placa. O SAAC gera uma identificação única no formato que o
        SGA aceita — ela aparece nas telas e no termo no lugar da placa.
      </>
    );

    return (
      <form ref={formRef} onSubmit={enviar} noValidate className="tw-flex tw-flex-col tw-gap-8">
        {aviso}
        {blocoAntes}

        {/* ── 1. Dados do veículo ───────────────────────────────────────── */}
        <FormSection titulo={tituloIdentificacao} icone="veiculo">
          <div className="tw-flex tw-flex-col tw-gap-4">
            {!ehBike && (
              <Checkbox
                rotulo="Veículo 0km (ainda sem placa)"
                descricao="Placa e renavam ficam para depois: o veículo é identificado pelo chassi e a placa é informada na Carteira quando sair."
                checked={ehZeroKm}
                onChange={alternarZeroKm}
              />
            )}

            {!ehZeroKm && (
              <Field
                rotulo={ehBike ? 'Identificação' : 'Placa'}
                obrigatorio={!ehBike}
                erro={erros.placa}
                campo="placa"
                dica={ehBike ? undefined : 'Formato antigo (AAA1234) ou Mercosul (AAA1A23).'}
              >
                <Input
                  type="text"
                  autoComplete="off"
                  value={form.placa}
                  readOnly={ehBike}
                  placeholder={ehBike ? 'Gerada ao salvar (ex.: BIK0001)' : 'ABC1D23'}
                  onChange={(evento) => definirPlaca(evento.target.value)}
                />
              </Field>
            )}

            {ehBike && (
              <p className="tw--mt-2 tw-text-sm tw-text-texto-suave">
                {dicaIdentificacaoBike ?? dicaBikePadrao}
              </p>
            )}

            {!ehZeroKm && acaoPlaca?.(form.placa)}

            <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
              <Field
                rotulo="Ano do Modelo"
                obrigatorio
                erro={erros.anoModelo}
                campo="anoModelo"
              >
                <Select
                  opcoes={anos}
                  placeholder="Selecione o ano"
                  value={form.anoModelo ? String(form.anoModelo) : ''}
                  onChange={(evento) => definirAno('anoModelo', evento.target.value)}
                />
              </Field>

              <Field
                rotulo="Ano de Fabricação"
                obrigatorio
                erro={erros.anoFabricacao}
                campo="anoFabricacao"
                dica={
                  form.anoModelo
                    ? 'Um veículo é fabricado no ano do modelo ou no anterior.'
                    : 'Escolha o ano do modelo primeiro.'
                }
              >
                <Select
                  opcoes={anosDeFabricacao}
                  placeholder="Selecione o ano"
                  value={form.anoFabricacao ? String(form.anoFabricacao) : ''}
                  onChange={(evento) => definirAno('anoFabricacao', evento.target.value)}
                />
              </Field>

              <Field rotulo="Marca" obrigatorio erro={erros.marcaId} campo="marcaId">
                <BotaoSelecao
                  valor={form.marcaNome}
                  vazio="Nenhuma marca selecionada"
                  temErro={!!erros.marcaId}
                  aoAbrir={() => setModalAberto('marcas')}
                />
              </Field>

              <Field rotulo="Modelo" obrigatorio erro={erros.modelo} campo="modelo">
                <BotaoSelecao
                  valor={form.modelo}
                  vazio="Nenhum veículo selecionado"
                  temErro={!!erros.modelo}
                  aoAbrir={abrirVeiculos}
                />
              </Field>
            </div>

            {!ehBike && (
              <Field rotulo="Código FIPE" campo="codigoFipe">
                <Input
                  type="text"
                  value={form.codigoFipe}
                  readOnly
                  placeholder="Carregado ao selecionar o modelo"
                />
              </Field>
            )}

            <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
              {/*
                at-54 (item 56): rótulo único. O campo segue OPCIONAL para elétrico
                — quem diz isso é o placeholder, e o backend gera o marcador
                `SEM-CHASSI-<placa>` quando vem vazio.
              */}
              <Field
                rotulo="Chassi"
                obrigatorio={!ehBike}
                erro={erros.chassi}
                campo="chassi"
              >
                <Input
                  type="text"
                  autoComplete="off"
                  maxLength={ehBike ? 30 : 17}
                  value={form.chassi}
                  placeholder={ehBike ? 'Gravado no quadro, se houver' : 'Digite o chassi'}
                  onChange={(evento) => aplicar({ chassi: evento.target.value })}
                />
              </Field>

              {!ehBike && !ehZeroKm && (
                <Field rotulo="Renavam" obrigatorio erro={erros.renavam} campo="renavam">
                  <Input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={11}
                    value={form.renavam}
                    placeholder="Digite o renavam"
                    onChange={(evento) => aplicar({ renavam: evento.target.value })}
                  />
                </Field>
              )}
            </div>
          </div>
        </FormSection>

        {/* ── 2. Informações gerais ─────────────────────────────────────── */}
        <FormSection titulo="Informações Gerais" icone="identificacao">
          <div className="tw-flex tw-flex-col tw-gap-4">
            <Field
              rotulo="Cooperativa"
              obrigatorio
              erro={erros.cooperativaId}
              campo="cooperativaId"
            >
              <BotaoSelecao
                valor={form.cooperativaNome}
                vazio="Nenhuma cooperativa selecionada"
                temErro={!!erros.cooperativaId}
                aoAbrir={() => setModalAberto('cooperativas')}
              />
            </Field>

            <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
              <Field rotulo="Cor" obrigatorio erro={erros.corId} campo="corId">
                <BotaoSelecao
                  valor={form.corNome}
                  vazio="Nenhuma cor selecionada"
                  temErro={!!erros.corId}
                  aoAbrir={() => setModalAberto('cores')}
                />
              </Field>

              <Field
                rotulo="Combustível"
                obrigatorio
                erro={erros.combustivelId}
                campo="combustivelId"
              >
                <BotaoSelecao
                  valor={form.combustivelNome}
                  vazio="Nenhum combustível selecionado"
                  temErro={!!erros.combustivelId}
                  aoAbrir={() => setModalAberto('combustiveis')}
                />
              </Field>

              <Field
                rotulo="Tipo de Veículo"
                obrigatorio
                erro={erros.tipoVeiculoId}
                campo="tipoVeiculoId"
                dica={
                  ehBike ? 'Definido automaticamente para motocicleta elétrica.' : undefined
                }
              >
                {/*
                  at-54 (item 58): veículo elétrico do catálogo tem UM tipo
                  possível, definido junto com o modelo. Deixar o botão ativo
                  convidaria a escolher um tipo que o backend descarta — e a lista
                  do modal nem oferece o correto.
                */}
                <BotaoSelecao
                  valor={form.tipoVeiculoNome}
                  vazio="Nenhum tipo de veículo selecionado"
                  temErro={!!erros.tipoVeiculoId}
                  desabilitado={ehBike}
                  title={ehBike ? 'Definido automaticamente para veículo elétrico' : undefined}
                  aoAbrir={() => setModalAberto('tipos')}
                />
              </Field>

              {/*
                ⚠️ Visível TAMBÉM para motocicleta elétrica, como nas telas antigas
                — e obrigatória só fora dela. O tipo 35 não tem categoria no SGA
                (at-46), então na prática o modal abre vazio; esconder o campo seria
                mudar o que a tela oferece, e isso é decisão de produto, não de
                refactor (`plan-c` §4.6.3).
              */}
              <Field
                rotulo="Categoria"
                obrigatorio={!ehBike}
                erro={erros.categoriaId}
                campo="categoriaId"
              >
                <BotaoSelecao
                  valor={form.categoriaNome}
                  vazio="Nenhuma categoria selecionada"
                  temErro={!!erros.categoriaId}
                  aoAbrir={abrirCategorias}
                />
              </Field>

              {!ehBike && (
                <Field rotulo="Câmbio" obrigatorio erro={erros.cambioId} campo="cambioId">
                  <Select
                    opcoes={[
                      { valor: 'MANUAL', rotulo: 'Manual' },
                      { valor: 'AUTOMATICO', rotulo: 'Automático' },
                    ]}
                    placeholder="Selecione"
                    value={form.cambioId}
                    onChange={(evento) => aplicar({ cambioId: evento.target.value })}
                  />
                </Field>
              )}
            </div>

            <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
              {!ehBike && (
                <Field
                  rotulo="Valor FIPE"
                  campo="valorFipe"
                  erro={erros.valorFipe}
                  dica={
                    referenciaFipe
                      ? `${ehZeroKm ? 'FIPE 0km · referência' : 'Referência FIPE'}: ${referenciaFipe}`
                      : undefined
                  }
                >
                  {/*
                    Somente leitura em todas as três telas: o valor vem do modelo
                    escolhido. (O `onChange` que existia era inalcançável — um
                    `readOnly` não dispara evento de digitação.)
                  */}
                  <Input
                    type="text"
                    value={form.valorFipe}
                    readOnly
                    placeholder="Carregado ao selecionar o modelo"
                  />
                </Field>
              )}

              <Field
                rotulo="Valor Protegido"
                obrigatorio
                erro={erros.valorProtegido}
                campo="valorProtegido"
                dica={
                  ehBike
                    ? 'Motocicleta elétrica não tem FIPE: este é o valor de referência do catálogo. É ELE que define a cota, o rateio e os produtos.'
                    : 'É ESTE valor que define a cota, o rateio e os produtos — não o valor FIPE.'
                }
              >
                {/*
                  at-30: digitação livre e formatação no blur. A máscara "em
                  centavos" que existia antes reescrevia o campo a cada tecla e
                  **jogava o cursor para o fim**, tornando impossível corrigir o
                  meio do valor — o usuário lia isso como "campo não editável".
                */}
                <Input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={form.valorProtegido}
                  readOnly={valorProtegidoTravado}
                  placeholder="Digite o valor"
                  onChange={(evento) =>
                    aplicar({ valorProtegido: digitarMoeda(evento.target.value), ...limparCota })
                  }
                  onBlur={() =>
                    setForm((antes) => ({
                      ...antes,
                      valorProtegido: normalizarMoeda(antes.valorProtegido),
                    }))
                  }
                />
              </Field>
            </div>

            {valorProtegidoTravado ? (
              <p className="tw-m-0 tw-rounded-xl tw-border tw-border-amber-warn/30 tw-bg-amber-bg tw-px-4 tw-py-3 tw-text-sm tw-text-amber-deep">
                {mensagemValorTravado}
              </p>
            ) : (
              !ehBike &&
              divergeAcimaDoLimiar(form.valorProtegido, form.valorFipe) && (
                <p className="tw-m-0 tw-rounded-xl tw-border tw-border-amber-warn/30 tw-bg-amber-bg tw-px-4 tw-py-3 tw-text-sm tw-text-amber-deep">
                  Atenção: o valor protegido difere mais de 20% do valor FIPE — confirme antes de
                  salvar.
                </p>
              )
            )}

            <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
              <Field rotulo="Cota" obrigatorio erro={erros.cotaId} campo="cotaId">
                <BotaoSelecao
                  valor={form.cotaDescricao}
                  vazio="Nenhuma cota selecionada"
                  temErro={!!erros.cotaId}
                  aoAbrir={abrirCotas}
                />
              </Field>

              <Field
                rotulo="Vencimento Desejado"
                obrigatorio
                erro={erros.vencimentoDesejado}
                campo="vencimentoDesejado"
              >
                <Select
                  opcoes={[
                    { valor: '10', rotulo: 'Dia 10' },
                    { valor: '20', rotulo: 'Dia 20' },
                  ]}
                  placeholder="Selecione o vencimento"
                  value={form.vencimentoDesejado ? String(form.vencimentoDesejado) : ''}
                  onChange={(evento) =>
                    aplicar({ vencimentoDesejado: parseInt(evento.target.value, 10) || 0 })
                  }
                />
              </Field>
            </div>
          </div>
        </FormSection>

        {/* ── 3. Endereço de cobrança ───────────────────────────────────── */}
        <FormSection titulo="Endereço de Cobrança" icone="endereco">
          <div className="tw-flex tw-flex-col tw-gap-4">
            <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-text-sm tw-font-semibold tw-text-texto-titulo">
              <input
                type="checkbox"
                className="tw-h-5 tw-w-5 tw-cursor-pointer tw-accent-brand"
                checked={usarEnderecoDoAssociado}
                onChange={(evento) => {
                  const marcado = evento.target.checked;
                  setUsarEnderecoDoAssociado(marcado);
                  setForm((antes) => ({
                    ...antes,
                    endereco: marcado ? { ...enderecoAssociado } : { ...ENDERECO_VAZIO },
                  }));
                }}
              />
              Mesmo endereço de cobrança do titular
            </label>

            {!usarEnderecoDoAssociado && (
              <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
                <Field rotulo="CEP" obrigatorio erro={erros.cep} campo="cep">
                  <Input
                    type="text"
                    inputMode="numeric"
                    autoComplete="postal-code"
                    maxLength={9}
                    placeholder="00000-000"
                    value={form.endereco.cep}
                    onChange={(evento) => definirEndereco('cep', evento.target.value)}
                  />
                </Field>

                <Field rotulo="Número" obrigatorio erro={erros.numero} campo="numero">
                  <Input
                    type="text"
                    autoComplete="off"
                    placeholder="123"
                    value={form.endereco.numero}
                    onChange={(evento) =>
                      definirEndereco('numero', evento.target.value.replace(/\D/g, ''))
                    }
                  />
                </Field>

                <Field
                  rotulo="Rua"
                  obrigatorio
                  erro={erros.rua}
                  campo="rua"
                  className="sm:tw-col-span-2"
                >
                  <Input
                    type="text"
                    autoComplete="street-address"
                    maxLength={100}
                    value={form.endereco.rua}
                    onChange={(evento) => definirEndereco('rua', evento.target.value)}
                  />
                </Field>

                <Field
                  rotulo="Complemento"
                  campo="complemento"
                  className="sm:tw-col-span-2"
                >
                  <Input
                    type="text"
                    autoComplete="off"
                    placeholder="Apto 42, Bloco B"
                    value={form.endereco.complemento}
                    onChange={(evento) => definirEndereco('complemento', evento.target.value)}
                  />
                </Field>

                <Field rotulo="Bairro" obrigatorio erro={erros.bairro} campo="bairro">
                  <Input
                    type="text"
                    autoComplete="address-level3"
                    value={form.endereco.bairro}
                    onChange={(evento) => definirEndereco('bairro', evento.target.value)}
                  />
                </Field>

                <Field rotulo="Cidade" obrigatorio erro={erros.cidade} campo="cidade">
                  <Input
                    type="text"
                    autoComplete="address-level2"
                    value={form.endereco.cidade}
                    onChange={(evento) => definirEndereco('cidade', evento.target.value)}
                  />
                </Field>

                <Field
                  rotulo="Estado (UF)"
                  obrigatorio
                  erro={erros.estado}
                  campo="estado"
                  className="sm:tw-col-span-2"
                >
                  <Select
                    opcoes={UFS}
                    placeholder="Selecione"
                    autoComplete="address-level1"
                    value={form.endereco.estado}
                    onChange={(evento) => definirEndereco('estado', evento.target.value)}
                  />
                </Field>
              </div>
            )}
          </div>
        </FormSection>

        <FormActions>
          <Botao
            type="submit"
            tamanho="lg"
            carregando={salvando}
            iconeDireita={salvando ? undefined : 'chevron-direita'}
            larguraTotal
            className="sm:tw-w-auto sm:tw-min-w-[220px]"
          >
            {salvando ? 'Salvando...' : rotuloAcao}
          </Botao>
        </FormActions>

        {/*
          📌 Os 8 modais foram migrados para o `Modal`/`ListaSelecao` do kit na
          própria at-59. As **props seguem em inglês**, e a at-62 mediu por quê:
          6 deles eram compartilhados com `/cotacao` — que agora também está
          migrada, de modo que **nenhum modal de escolha tem mais consumidor
          legado**. Traduzir as props virou trabalho possível, mas mecânico e
          transversal: é da frente D, não de uma branch de tela.
        */}
        <ModalMarcas
          isOpen={modalAberto === 'marcas'}
          onClose={fechar}
          onSelectMarca={selecionarMarca}
          incluirBikes
        />
        <ModalVeiculos
          isOpen={modalAberto === 'veiculos'}
          onClose={fechar}
          onSelectVeiculo={selecionarVeiculo}
          year={form.anoModelo}
          brand={form.marcaNome}
          bikeMarcaId={form.bikeMarcaId || null}
          zeroKm={ehZeroKm}
        />
        <ModalCores
          isOpen={modalAberto === 'cores'}
          onClose={fechar}
          onSelectCor={(cor: any) => {
            aplicar({ corId: cor.codigo_cor, corNome: cor.descricao });
            fechar();
          }}
        />
        <ModalCombustiveis
          isOpen={modalAberto === 'combustiveis'}
          onClose={fechar}
          onSelectCombustivel={(combustivel: any) => {
            aplicar({
              combustivelId: combustivel.codigo_combustivel,
              combustivelNome: combustivel.descricao,
            });
            fechar();
          }}
        />
        <ModalTipoVeiculos
          isOpen={modalAberto === 'tipos'}
          onClose={fechar}
          onSelectTipoVeiculo={selecionarTipo}
        />
        <ModalCategorias
          isOpen={modalAberto === 'categorias'}
          onClose={fechar}
          onSelectCategoria={(categoria: any) => {
            aplicar({
              categoriaId: categoria.codigo_categoria,
              categoriaNome: categoria.descricao_categoria,
            });
            fechar();
          }}
          /*
           * ⚠️ Vem do CAMPO, não de um estado paralelo. As telas antigas mantinham
           * um `codigoTipo` que só `handleSelectTipoVeiculo` alimentava — então em
           * `/carro/substituir`, depois de carregar um veículo pela placa, o tipo
           * aparecia preenchido na tela mas "Selecionar Categoria" respondia
           * "Selecione um Tipo de Veículo antes de continuar".
           */
          codigoTipo={form.tipoVeiculoId}
        />
        <ModalCooperativasUsuario
          isOpen={modalAberto === 'cooperativas'}
          onClose={fechar}
          onSelectCooperativa={(cooperativa: any) => {
            aplicar({
              cooperativaId: cooperativa.codigo_cooperativa,
              cooperativaNome: cooperativa.nome,
              ...limparCota,
            });
            fechar();
          }}
        />
        <ModalCotas
          isOpen={modalAberto === 'cotas'}
          onClose={fechar}
          onSelectCota={(cota: any) => {
            aplicar({
              cotaId: cota.codigo_cota,
              cotaDescricao: cota.descricao_cota,
              valorCota: cota.valor_cota,
            });
            fechar();
          }}
          codigoRegional={regionalId}
          codigoCooperativa={form.cooperativaId}
          codigoTipoVeiculo={form.tipoVeiculoId}
          /* A faixa é a do valor PROTEGIDO (at-30); a FIPE é só o fallback. */
          valorFipe={(form.valorProtegido || form.valorFipe).toString()}
        />
      </form>
    );
  },
);

FormularioVeiculo.displayName = 'FormularioVeiculo';

export default FormularioVeiculo;
